import { cn, Dialog, DialogContent, DialogHeader, DialogTitle } from '@newfront-insurance/core-ui/v2';
import { AlertTriangle, Check, Download, FolderArchive, FolderDown, Loader2 } from 'lucide-react';

import type { FolderApplyPlan, FolderApplyStatus } from '../lib/folder-artifact-apply';

function getFolderDisplayName(filename: string): string {
  return filename.replace(/\.zip$/i, '').trim() || filename;
}

function getStatusLabel(status: FolderApplyStatus) {
  if (status === 'create') return 'Add';
  if (status === 'overwrite') return 'Replace';
  return 'Conflict';
}

function getStatusClassName(status: FolderApplyStatus) {
  if (status === 'create') return 'bg-[#E9F7EF] text-[#2E6F45]';
  if (status === 'overwrite') return 'bg-[#FFF4D6] text-[#7A5B00]';
  return 'bg-[#FDEAEA] text-[#9B1C1C]';
}

function FolderApplyDialog({
  folderName,
  isApplying,
  onCancel,
  onConfirm,
  plan,
}: {
  folderName: string;
  isApplying?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  plan: FolderApplyPlan | null;
}) {
  const previewEntries = plan?.entries.slice(0, 6) ?? [];
  const remainingCount = Math.max((plan?.entries.length ?? 0) - previewEntries.length, 0);
  const hasConflicts = Boolean(plan?.conflictCount);

  return (
    <Dialog open={Boolean(plan)} onOpenChange={(open) => !open && !isApplying && onCancel?.()}>
      <DialogContent className="max-w-[520px] p-0">
        <DialogHeader className="border-b border-[#D7E0EA] px-5 py-4">
          <DialogTitle>Save changes to {folderName}</DialogTitle>
        </DialogHeader>
        {plan ? (
          <div className="space-y-4 px-5 py-4">
            <div className="space-y-1">
              <div className="text-[14px] leading-5 font-medium text-[#262D46]">{plan.targetFolderName}</div>
              <div className="text-[12px] leading-4 text-[#546A83]">
                {plan.createCount} to add, {plan.overwriteCount} to replace
                {plan.conflictCount ? `, ${plan.conflictCount} conflict${plan.conflictCount === 1 ? '' : 's'}` : ''}
              </div>
            </div>

            {hasConflicts ? (
              <div className="flex items-start gap-2 rounded-md border border-[#F3C6C6] bg-[#FFF7F7] px-3 py-2 text-[12px] leading-4 text-[#7A1C1C]">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span>Choose a folder without file and folder name conflicts before applying.</span>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md border border-[#CFE8D8] bg-[#F7FCF9] px-3 py-2 text-[12px] leading-4 text-[#2E6F45]">
                <Check className="mt-0.5 size-4 shrink-0" />
                <span>Nova will only add or replace files.</span>
              </div>
            )}

            <div className="max-h-48 overflow-auto rounded-md border border-[#D7E0EA] bg-white">
              {previewEntries.map((entry) => (
                <div
                  key={entry.path}
                  className="flex items-center gap-3 border-b border-[#EEF2F6] px-3 py-2 last:border-b-0"
                >
                  <span
                    className={cn(
                      'w-[62px] shrink-0 rounded px-2 py-0.5 text-center text-[11px] leading-4 font-medium',
                      getStatusClassName(entry.status),
                    )}
                  >
                    {getStatusLabel(entry.status)}
                  </span>
                  <span className="min-w-0 truncate text-[12px] leading-4 text-[#262D46]">{entry.path}</span>
                </div>
              ))}
              {remainingCount > 0 ? (
                <div className="px-3 py-2 text-[12px] leading-4 text-[#546A83]">
                  {remainingCount} more file{remainingCount === 1 ? '' : 's'}
                </div>
              ) : null}
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onCancel}
                disabled={isApplying}
                className="rounded-md px-3 py-2 text-[13px] font-medium text-[#546A83] transition-colors hover:bg-[#EEF2F6] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={isApplying || hasConflicts}
                className={cn(
                  'bg-brand-400 hover:bg-brand-500 flex items-center gap-2 rounded-md px-3 py-2',
                  'text-[13px] font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                )}
              >
                {isApplying ? <Loader2 className="size-4 animate-spin" /> : null}
                Save changes
              </button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function FolderArtifactCard({
  applyPlan,
  filename,
  isApplying = false,
  isApplySupported = false,
  onDownload,
  onApply,
  onCancelApply,
  onConfirmApply,
  isDownloading = false,
  isPreparingApply = false,
}: {
  applyPlan?: FolderApplyPlan | null;
  filename: string;
  isApplying?: boolean;
  isApplySupported?: boolean;
  onDownload?: () => void;
  onApply?: () => void;
  onCancelApply?: () => void;
  onConfirmApply?: () => void;
  isDownloading?: boolean;
  isPreparingApply?: boolean;
}) {
  const folderName = getFolderDisplayName(filename);
  const canApply = isApplySupported && Boolean(onApply);

  return (
    <div className="flex flex-col gap-3">
      <div className="px-[10px]">
        <div className="bg-muted flex items-center justify-between gap-4 overflow-hidden rounded-lg border border-[#D7E0EA] px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-white text-[#546A83]">
              <FolderArchive className="size-5" />
            </div>
            <div className="min-w-0 space-y-1">
              <div className="truncate text-[14px] leading-5 font-semibold text-[#262D46]">{folderName}</div>
              <div className="truncate text-[12px] leading-4 text-[#546A83]">Folder ready.</div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canApply ? (
              <button
                type="button"
                onClick={onApply}
                disabled={isPreparingApply || isApplying}
                className={cn(
                  'flex items-center gap-2 rounded-md bg-white px-3 py-2 text-[13px] font-medium text-[#546A83]',
                  'transition-colors hover:text-[#262D46] disabled:cursor-not-allowed disabled:opacity-50',
                )}
                aria-label={`Save ${folderName} to folder`}
              >
                {isPreparingApply ? <Loader2 className="size-4 animate-spin" /> : <FolderDown className="size-4" />}
                Save to folder
              </button>
            ) : null}
            <button
              type="button"
              onClick={onDownload}
              disabled={!onDownload || isDownloading}
              className={cn('text-[#9CA9BA] transition-colors hover:text-[#546A83]', {
                'cursor-not-allowed opacity-50': !onDownload || isDownloading,
              })}
              aria-label={`Download ${folderName}`}
            >
              {isDownloading ? <Loader2 className="size-6 animate-spin" /> : <Download className="size-6" />}
            </button>
          </div>
        </div>
      </div>
      <FolderApplyDialog
        folderName={folderName}
        isApplying={isApplying}
        onCancel={onCancelApply}
        onConfirm={onConfirmApply}
        plan={applyPlan ?? null}
      />
    </div>
  );
}
